import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';

export type SheenProps = {
  children: ReactNode;
  trigger?: 'inView' | 'hover' | 'none';
  className?: string;
  skew?: number;
  durationMs?: number;
};

/**
 * Wraps a child (the hero mock, a card) and sweeps a single skewed,
 * ink-tinted translucent bar across it ONCE on inView or on hover —
 * never idle-loops. Uses the `sheen` keyframe via .sheen-sweep.
 * Disabled entirely under prefers-reduced-motion.
 */
export function Sheen({
  children,
  trigger = 'inView',
  className,
  skew = 12,
  durationMs = 1100,
}: SheenProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const fired = useRef(false);
  const [run, setRun] = useState(false);

  // inView trigger — fire once when the container enters the viewport.
  useEffect(() => {
    if (reduce || trigger !== 'inView') return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired.current) {
            fired.current = true;
            setRun(true);
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, trigger]);

  const handleEnter = () => {
    if (reduce || trigger !== 'hover') return;
    // Restart the one-shot on each hover.
    setRun(false);
    requestAnimationFrame(() => setRun(true));
  };

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${className ?? ''}`.trim()}
      onMouseEnter={handleEnter}
    >
      {children}
      {!reduce && (
        <span
          aria-hidden="true"
          className="sheen-sweep"
          data-run={run ? 'true' : 'false'}
          style={
            {
              '--sheen-duration': `${durationMs}ms`,
              transform: `translateX(-120%) skewX(-${skew}deg)`,
            } as React.CSSProperties
          }
        />
      )}
    </div>
  );
}
